import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useCandidate } from '@/hooks/use-candidates';
import { usePermission } from '@/lib/permissions';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth';
import type { CandidateDetail } from '@/types/candidates';
import type {
  PipelineCard,
  PipelineStageBucket,
  VacancyDetail,
} from '@/types/recruitment';
import {
  ArrowLeftRight,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Plus,
  ShieldAlert,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { AUDIT_FILTERS } from '../lib/audit-event-renderer';
import { AssignCandidateDialog } from './assign-candidate-dialog';
import { AuditLogFeed } from './audit-log-feed';
import { CandidateAssignmentsTable } from './candidate-assignments-table';
import { CandidateConsentBanner } from './candidate-consent-banner';
import { CandidateFilesTab } from './candidate-files-tab';
import { CandidateGdprTab } from './candidate-gdpr-tab';
import { CandidateInterviewTab } from './candidate-interview-tab';
import { CandidateNotesFeed } from './candidate-notes-feed';
import { CandidateProfileContent } from './candidate-profile-content';
import { CandidateProfileHeader } from './candidate-profile-header';
import { CandidateParseJobProvider } from '../context/candidate-parse-job-context';
import { CONSENT_LOCKED_HINT, isConsentLocked } from '../lib/consent-actions';

type ModalTab =
  | 'profile'
  | 'vacancies'
  | 'notes'
  | 'interview'
  | 'files'
  | 'audit'
  | 'gdpr';

type AuditFilterKey = (typeof AUDIT_FILTERS)[number]['key'];

const TABS: Array<{ slug: ModalTab; label: string }> = [
  { slug: 'profile', label: 'Profile' },
  { slug: 'vacancies', label: 'Vacancies' },
  { slug: 'notes', label: 'Notes' },
  { slug: 'interview', label: 'Interview' },
  { slug: 'files', label: 'Files' },
  { slug: 'audit', label: 'Audit Log' },
  { slug: 'gdpr', label: 'GDPR' },
];

interface CandidateProfileModalProps {
  vacancy: VacancyDetail;
  card: PipelineCard | null;
  stage: PipelineStageBucket | null;
  /** All pipeline stages of the current vacancy, for the in-modal stage selector. */
  stages: PipelineStageBucket[];
  /** Move the candidate to another stage; omitted when the user can't move cards. */
  onMoveStage?: (stageId: string) => void;
  /** A stage move is in flight — disables the selector to avoid double-submits. */
  isMoving?: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPrev?: () => void;
  onNext?: () => void;
  hasPrev: boolean;
  hasNext: boolean;
  position: { index: number; total: number } | null;
}

export function CandidateProfileModal({
  vacancy,
  card,
  stage,
  stages,
  onMoveStage,
  isMoving,
  open,
  onOpenChange,
  onPrev,
  onNext,
  hasPrev,
  hasNext,
  position,
}: CandidateProfileModalProps) {
  const candidateId = card?.candidate.id ?? '';
  const { data: candidate, isLoading, error } = useCandidate(candidateId);

  const [tab, setTab] = useState<ModalTab>('profile');
  const [auditFilter, setAuditFilter] = useState<AuditFilterKey>('all');
  const [assignOpen, setAssignOpen] = useState(false);

  // Reset tab when switching candidates.
  useEffect(() => {
    if (candidateId) setTab('profile');
  }, [candidateId]);

  // Keyboard navigation: ←/→ to walk the column.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable ||
          target.closest('[role="menu"], [role="listbox"], [role="tablist"]'))
      ) {
        return;
      }
      if (e.key === 'ArrowLeft' && hasPrev) {
        e.preventDefault();
        onPrev?.();
      } else if (e.key === 'ArrowRight' && hasNext) {
        e.preventDefault();
        onNext?.();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, hasPrev, hasNext, onPrev, onNext]);

  if (!card || !stage) return null;

  const fullName = `${card.candidate.firstName} ${card.candidate.lastName}`.trim();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'flex h-[90vh] w-[95vw] max-w-[1400px] flex-col gap-0 overflow-hidden p-0',
        )}
      >
        <DialogTitle className="sr-only">{fullName}</DialogTitle>
        <DialogDescription className="sr-only">
          Candidate profile — current stage {stage.name} in {vacancy.title}
        </DialogDescription>

        <ModalTopBar
          vacancy={vacancy}
          card={card}
          stage={stage}
          stages={stages}
          onMoveStage={onMoveStage}
          isMoving={isMoving}
          hasPrev={hasPrev}
          hasNext={hasNext}
          onPrev={onPrev}
          onNext={onNext}
          position={position}
          onRequestAssign={() => setAssignOpen(true)}
          consentLocked={isConsentLocked(candidate)}
        />

        <CandidateParseJobProvider candidateId={candidateId}>
          <main className="flex min-h-0 flex-1 flex-col overflow-hidden">
            {isLoading ? (
              <LoadingState />
            ) : error || !candidate ? (
              <ErrorState />
            ) : (
              <>
                <div className="shrink-0 px-6 pt-5">
                  <CandidateProfileHeader candidate={candidate} />
                  <CandidateConsentBanner candidate={candidate} showGdprLink={false} />
                </div>
                <TabStrip
                  active={tab}
                  onChange={setTab}
                  isRejected={card.result === 'REJECTED'}
                />
                <div className="flex-1 overflow-y-auto px-6 py-5">
                  <TabBody
                    tab={tab}
                    candidate={candidate}
                    candidateId={candidateId}
                    auditFilter={auditFilter}
                    onAuditFilterChange={setAuditFilter}
                  />
                </div>
              </>
            )}
          </main>
        </CandidateParseJobProvider>

        {candidate && (
          <AssignCandidateDialog
            candidate={{
              id: candidate.id,
              fullName: candidate.fullName,
              activeAssignments: candidate.assignments
                .filter((a) => a.result === 'IN_PROGRESS')
                .map((a) => ({ vacancyId: a.vacancyId })),
            }}
            open={assignOpen}
            onOpenChange={setAssignOpen}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

interface ModalTopBarProps {
  vacancy: VacancyDetail;
  card: PipelineCard;
  stage: PipelineStageBucket;
  stages: PipelineStageBucket[];
  onMoveStage?: (stageId: string) => void;
  isMoving?: boolean;
  hasPrev: boolean;
  hasNext: boolean;
  onPrev?: () => void;
  onNext?: () => void;
  position: { index: number; total: number } | null;
  onRequestAssign: () => void;
  /** HW-1055: consent revoked — no Assign, and Move only into a rejection stage. */
  consentLocked: boolean;
}

function ModalTopBar({
  vacancy,
  card,
  stage,
  stages,
  onMoveStage,
  isMoving,
  hasPrev,
  hasNext,
  onPrev,
  onNext,
  position,
  onRequestAssign,
  consentLocked,
}: ModalTopBarProps) {
  const isRejected = card.result === 'REJECTED';
  const timeInStage = formatTimeInStage(new Date(card.stageEnteredAt));
  // The line below truncates, so the tooltip has to carry what the row renders — in this
  // header the rejection reason is otherwise only readable a tab switch away, on Vacancies.
  const stageLine =
    `${stage.name} · in stage ${timeInStage}` +
    (isRejected
      ? ` · Rejected${card.rejectionReason ? ` — ${card.rejectionReason.label}` : ''}`
      : '');

  const sortedStages = useMemo(
    () => [...stages].sort((a, b) => a.position - b.position),
    [stages],
  );

  return (
    <div className="flex h-12 shrink-0 items-center gap-3 border-b border-border bg-background px-5 pr-14">
      <div className="flex min-w-0 flex-col">
        <span
          className="truncate text-sm font-medium text-foreground"
          title={vacancy.title}
        >
          {vacancy.title}
        </span>
        <span className="truncate text-[11px] text-muted-foreground" title={stageLine}>
          {stage.name} <span className="opacity-60">· in stage {timeInStage}</span>
          {isRejected && (
            <span className="ml-1 text-red-600">
              · Rejected{card.rejectionReason ? ` — ${card.rejectionReason.label}` : ''}
            </span>
          )}
        </span>
      </div>

      <div className="ml-auto flex items-center gap-2">
        {onMoveStage && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" disabled={isMoving}>
                <ArrowLeftRight className="h-3.5 w-3.5" />
                Move
                <ChevronDown className="h-3.5 w-3.5 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>Move to stage</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {sortedStages.map((s) => {
                const isCurrent = s.id === stage.id;
                // HW-1055: a candidate who revoked consent can only be closed out
                // into a rejection (ARCHIVED-mapped) stage — the backend refuses
                // any other move.
                const lockedOut = consentLocked && s.candidateStatus !== 'ARCHIVED';
                return (
                  <DropdownMenuItem
                    key={s.id}
                    disabled={isCurrent || lockedOut}
                    onSelect={(e) => {
                      if (isCurrent) {
                        e.preventDefault();
                        return;
                      }
                      onMoveStage(s.id);
                    }}
                    className="justify-between"
                  >
                    <span className="truncate" title={s.name}>
                      {s.name}
                    </span>
                    {isCurrent && <Check className="h-3.5 w-3.5 shrink-0 opacity-60" />}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        <span title={consentLocked ? CONSENT_LOCKED_HINT : undefined}>
          <Button
            variant="outline"
            size="sm"
            onClick={onRequestAssign}
            disabled={consentLocked}
          >
            <Plus className="h-3.5 w-3.5" />
            Assign
          </Button>
        </span>

        <div className="mx-2 h-5 w-px bg-border" aria-hidden />

        {position && (
          <span className="text-xs tabular-nums text-muted-foreground">
            {position.index + 1} / {position.total}
          </span>
        )}
        <button
          type="button"
          onClick={() => hasPrev && onPrev?.()}
          disabled={!hasPrev}
          aria-label="Previous candidate"
          className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => hasNext && onNext?.()}
          disabled={!hasNext}
          aria-label="Next candidate"
          className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

interface TabStripProps {
  active: ModalTab;
  onChange: (tab: ModalTab) => void;
  isRejected: boolean;
}

function TabStrip({ active, onChange, isRejected }: TabStripProps) {
  return (
    <div
      role="tablist"
      className="shrink-0 border-b border-border bg-background px-6"
    >
      <nav className="flex gap-6 overflow-x-auto">
        {TABS.map((t) => {
          const isActive = t.slug === active;
          return (
            <button
              key={t.slug}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => onChange(t.slug)}
              className={cn(
                'relative cursor-pointer whitespace-nowrap pb-3 pt-3 text-sm font-medium transition-colors',
                isActive
                  ? "text-foreground after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:rounded-t-full after:bg-hygge after:content-['']"
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {t.label}
              {t.slug === 'vacancies' && isRejected && (
                <span
                  title="Rejected on current vacancy"
                  className="ml-1 inline-block h-1.5 w-1.5 rounded-full bg-red-500 align-middle"
                />
              )}
            </button>
          );
        })}
      </nav>
    </div>
  );
}

interface TabBodyProps {
  tab: ModalTab;
  candidate: CandidateDetail;
  candidateId: string;
  auditFilter: AuditFilterKey;
  onAuditFilterChange: (filter: AuditFilterKey) => void;
}

function TabBody({
  tab,
  candidate,
  candidateId,
  auditFilter,
  onAuditFilterChange,
}: TabBodyProps) {
  const currentUserId = useAuthStore((s) => s.user?.employee?.id ?? null);
  const canViewGdpr = usePermission('recruitment.candidates.gdpr');

  switch (tab) {
    case 'profile':
      return <CandidateProfileContent candidate={candidate} />;
    case 'vacancies':
      return <CandidateAssignmentsTable candidateId={candidateId} />;
    case 'notes':
      return <CandidateNotesFeed candidateId={candidateId} currentUserId={currentUserId} />;
    case 'interview':
      return <CandidateInterviewTab candidateId={candidateId} />;
    case 'files':
      return <CandidateFilesTab candidateId={candidateId} />;
    case 'audit':
      return (
        <AuditLogFeed
          candidateId={candidateId}
          filterKey={auditFilter}
          onFilterChange={(key) => onAuditFilterChange(key as AuditFilterKey)}
        />
      );
    case 'gdpr':
      if (!canViewGdpr) {
        return (
          <div className="flex flex-col items-center rounded-lg border border-dashed border-border bg-muted/30 p-8 text-center">
            <ShieldAlert className="h-8 w-8 text-muted-foreground/60" aria-hidden />
            <p className="mt-2 text-sm font-medium text-foreground">GDPR data is restricted</p>
            <p className="mt-1 max-w-md text-xs text-muted-foreground">
              You need the <span className="font-mono">recruitment.candidates.gdpr</span>{' '}
              permission to view consent history.
            </p>
          </div>
        );
      }
      return <CandidateGdprTab candidate={candidate} />;
    default:
      return null;
  }
}

function formatTimeInStage(date: Date): string {
  const diffMs = Date.now() - date.getTime();
  if (diffMs < 24 * 60 * 60 * 1000) {
    const hours = Math.floor(diffMs / (60 * 60 * 1000));
    return hours <= 0 ? 'just now' : `${hours}h`;
  }
  const days = Math.floor(diffMs / (24 * 60 * 60 * 1000));
  if (days < 30) return `${days}d`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo`;
  return `${Math.floor(days / 365)}y`;
}

function LoadingState() {
  return (
    <div className="flex-1 px-6 py-12 text-sm text-muted-foreground">Loading candidate…</div>
  );
}

function ErrorState() {
  return (
    <div className="m-6 rounded-md border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
      Failed to load candidate.
    </div>
  );
}
